import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SITE } from "@/config/site";
import { Footer } from "@/components/landing/Footer";
import { CookieConsent } from "@/components/landing/CookieConsent";

const LegalLayout = ({ title, children, testId }) => (
  <main className="min-h-svh bg-navy-deep text-cream" data-testid={testId}>
    <header className="border-b border-white/[0.06]">
      <div className="mx-auto flex h-[72px] max-w-3xl items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          className="font-serif text-xl tracking-wide text-cream"
          data-testid="legal-logo"
        >
          Proměna <span className="text-copper">muže</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-cream/60 transition-colors hover:text-copper"
          data-testid="legal-back"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Zpět na web
        </Link>
      </div>
    </header>
    <article className="mx-auto max-w-3xl px-5 py-20 sm:px-8">
      <h1 className="font-serif text-4xl font-medium tracking-tight sm:text-5xl">{title}</h1>
      <div className="mt-12 space-y-10 text-[15px] leading-relaxed text-cream/75">{children}</div>
    </article>
    <Footer />
    <CookieConsent />
  </main>
);

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl font-medium text-cream">{children}</h2>
);

export function TermsPage() {
  return (
    <LegalLayout title="Obchodní podmínky" testId="terms-page">
      <section className="space-y-3">
        <H2>1. Prodávající</H2>
        <p>
          Prodávajícím je {SITE.operator.name}, IČO: {SITE.operator.ico}, se sídlem{" "}
          {SITE.operator.address}, zapsaný v živnostenském rejstříku, provozovatel projektu
          Proměna muže (promenamuze.cz).
        </p>
        <p>
          Kontakt:{" "}
          <a href={`mailto:${SITE.contactEmail}`} className="text-copper underline underline-offset-2">
            {SITE.contactEmail}
          </a>
        </p>
      </section>

      <section className="space-y-3">
        <H2>2. Předmět prodeje</H2>
        <p>
          Předmětem prodeje je digitální e-book „40 principů lepšího muže“ ve formátu PDF
          (dále jen „e-book“). E-book je dodáván výhradně v elektronické podobě; nejde o tištěnou
          knihu.
        </p>
      </section>

      <section className="space-y-3">
        <H2>3. Cena a platební podmínky</H2>
        <p>
          Aktuální cena e-booku je vždy uvedena na webu promenamuze.cz u objednávkového tlačítka a
          v objednávkovém formuláři. Při spuštění prodeje činí zaváděcí cena {SITE.introPrice} Kč
          pro prvních {SITE.introLimit} prodaných e-booků, následně {SITE.futurePrice} Kč. Jde o
          jednorázovou platbu – nejde o předplatné. Prodávající není plátcem DPH; uvedená cena je
          konečná.
        </p>
        <p>
          Objednávka a platba probíhají online prostřednictvím objednávkového formuláře služby
          SimpleShop (SimpleShop s.r.o.) a napojené platební brány. Daňový doklad a objednávkový
          proces zajišťuje použitý prodejní systém podle jeho skutečného nastavení.
        </p>
      </section>

      <section className="space-y-3">
        <H2>4. Dodání digitálního obsahu</H2>
        <p>
          Po úspěšném zaplacení získá kupující elektronický přístup k e-booku – odkaz ke stažení
          PDF je zaslán na e-mail uvedený v objednávce. E-book lze stáhnout ihned po připsání
          platby.
        </p>
      </section>

      <section className="space-y-3">
        <H2>5. Odstoupení od smlouvy</H2>
        <p>
          Kupující v objednávce uděluje výslovný souhlas se zpřístupněním digitálního obsahu před
          uplynutím 14denní lhůty pro odstoupení od smlouvy a potvrzuje, že byl poučen o tom, že
          tím podle § 1837 písm. l) zákona č. 89/2012 Sb., občanský zákoník, zaniká právo na
          odstoupení od smlouvy. Na e-book se proto právo odstoupit od smlouvy ve 14denní lhůtě
          nevztahuje.
        </p>
      </section>

      <section className="space-y-3">
        <H2>6. Reklamace</H2>
        <p>
          V případě technického problému se stažením nebo otevřením souboru kontaktuj
          prodávajícího na {SITE.contactEmail}. Reklamace bude vyřízena bez zbytečného odkladu,
          nejpozději do 30 dnů od jejího uplatnění.
        </p>
      </section>

      <section className="space-y-3">
        <H2>7. Autorská práva a licence</H2>
        <p>
          E-book je chráněn autorským právem. Koupí získává kupující nevýhradní licenci k osobnímu
          užití e-booku. Jakékoli další šíření, kopírování, prodávání nebo poskytování e-booku
          třetím osobám je bez souhlasu autora zakázáno.
        </p>
      </section>

      <section className="space-y-3">
        <H2>8. Mimosoudní řešení sporů</H2>
        <p>
          K mimosoudnímu řešení spotřebitelských sporů z kupní smlouvy je příslušná Česká obchodní
          inspekce (www.coi.cz). Spotřebitel může využít také platformu pro řešení sporů online na
          adrese ec.europa.eu/consumers/odr.
        </p>
      </section>

      <section className="space-y-3">
        <H2>9. Závěrečná ustanovení</H2>
        <p>
          Právní vztahy mezi prodávajícím a kupujícím se řídí právním řádem České republiky. Tyto
          obchodní podmínky nabývají účinnosti dnem zveřejnění na webu promenamuze.cz.
        </p>
      </section>
    </LegalLayout>
  );
}

export function PrivacyPage() {
  return (
    <LegalLayout title="Ochrana osobních údajů" testId="privacy-page">
      <section className="space-y-3">
        <H2>1. Správce osobních údajů</H2>
        <p>
          Správcem osobních údajů je provozovatel projektu Proměna muže. Jeho úplné identifikační
          údaje najdeš na stránce{" "}
          <Link to="/kontakt" className="text-copper underline underline-offset-2">
            Kontakt
          </Link>{" "}
          a v{" "}
          <Link to="/obchodni-podminky" className="text-copper underline underline-offset-2">
            obchodních podmínkách
          </Link>
          . Kontakt:{" "}
          <a href={`mailto:${SITE.contactEmail}`} className="text-copper underline underline-offset-2">
            {SITE.contactEmail}
          </a>
        </p>
      </section>

      <section className="space-y-3">
        <H2>2. Jaké údaje zpracováváme a proč</H2>
        <p>
          <strong className="text-cream">Objednávka e-booku.</strong> Údaje zadané při nákupu
          (jméno, e-mail, případné fakturační údaje) jsou zpracovávány prostřednictvím prodejní a
          platební platformy SimpleShop s.r.o. za účelem uzavření a plnění kupní smlouvy,
          vystavení daňového dokladu a doručení e-booku.
        </p>
        <p>
          <strong className="text-cream">Kontaktní e-mail.</strong> Při dotazu na{" "}
          {SITE.contactEmail} zpracováváme tvou e-mailovou adresu a obsah zprávy za účelem
          vyřízení dotazu.
        </p>
        <p>
          <strong className="text-cream">Analytika a marketing.</strong> Nástroje Google Analytics
          4, Google Tag Manager a Meta Pixel používáme pouze v případě, že jim udělíš souhlas v
          cookie liště. Bez souhlasu se tyto nástroje nenačítají a žádná analytická ani
          marketingová data se nesbírají.
        </p>
      </section>

      <section className="space-y-3">
        <H2>3. Cookies</H2>
        <p>
          Web používá nezbytné cookies potřebné k jeho fungování (například uložení tvé volby v
          cookie liště). Analytické a marketingové cookies se spouštějí až po udělení souhlasu.
          Svou volbu můžeš kdykoli změnit vymazáním cookies tohoto webu v nastavení prohlížeče –
          cookie lišta se poté zobrazí znovu.
        </p>
      </section>

      <section className="space-y-3">
        <H2>4. Tvá práva</H2>
        <p>
          Máš právo na přístup k osobním údajům, jejich opravu nebo výmaz, omezení zpracování,
          právo vznést námitku a právo podat stížnost u Úřadu pro ochranu osobních údajů
          (uoou.gov.cz). Svá práva uplatníš na {SITE.contactEmail}.
        </p>
      </section>

      <section className="space-y-3">
        <H2>5. Doba uchování</H2>
        <p>
          Osobní údaje uchováváme po dobu nezbytnou k plnění smlouvy a následně po dobu stanovenou
          právními předpisy, zejména účetními a daňovými (obvykle 10 let od uskutečnění plnění).
        </p>
      </section>
    </LegalLayout>
  );
}

export function ContactPage() {
  return (
    <LegalLayout title="Kontakt" testId="contact-page">
      <section className="space-y-3">
        <H2>Napiš mi</H2>
        <p>
          Máš dotaz k e-booku, objednávce nebo spolupráci? Ozvi se na:{" "}
          <a
            href={`mailto:${SITE.contactEmail}`}
            className="text-copper underline underline-offset-2"
            data-testid="contact-email"
          >
            {SITE.contactEmail}
          </a>
        </p>
        <p>Odpovídám zpravidla do několika pracovních dnů.</p>
      </section>

      <section className="space-y-3">
        <H2>Objednávky a fakturace</H2>
        <p>
          Platby, objednávky a daňové doklady zajišťuje služba SimpleShop. Pokud ti nedorazil
          e-mail s odkazem ke stažení, zkontroluj prosím složku Spam, případně se ozvi na e-mailu
          výše.
        </p>
      </section>

      <section className="space-y-3">
        <H2>Provozovatel</H2>
        <p>
          {SITE.operator.name}
          <br />
          IČO: {SITE.operator.ico}
          <br />
          Sídlo: {SITE.operator.address}
          <br />
          E-mail:{" "}
          <a
            href={`mailto:${SITE.contactEmail}`}
            className="text-copper underline underline-offset-2"
            data-testid="contact-operator-email"
          >
            {SITE.contactEmail}
          </a>
          <br />
          Proměna muže · promenamuze.cz
        </p>
      </section>
    </LegalLayout>
  );
}
